import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Check, GraduationCap, BriefcaseIcon, Plane, MapPin, UserCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

type SurveyStep = "destination" | "purpose" | "location" | "contact" | "complete";

interface CountryOption {
  value: string;
  label: string;
  flag: string;
}

const countryOptions: CountryOption[] = [
  { value: "usa", label: "United States", flag: "🇺🇸" },
  { value: "canada", label: "Canada", flag: "🇨🇦" },
  { value: "uk", label: "United Kingdom", flag: "🇬🇧" },
  { value: "australia", label: "Australia", flag: "🇦🇺" },
  { value: "france", label: "France", flag: "🇫🇷" },
  { value: "germany", label: "Germany", flag: "🇩🇪" },
  { value: "other", label: "Other Countries", flag: "🌎" },
];

const allCountries: CountryOption[] = [
  { value: "afghanistan", label: "Afghanistan", flag: "🇦🇫" },
  { value: "albania", label: "Albania", flag: "🇦🇱" },
  { value: "algeria", label: "Algeria", flag: "🇩🇿" },
  { value: "andorra", label: "Andorra", flag: "🇦🇩" },
  { value: "angola", label: "Angola", flag: "🇦🇴" },
  { value: "antigua_and_barbuda", label: "Antigua and Barbuda", flag: "🇦🇬" },
  { value: "argentina", label: "Argentina", flag: "🇦🇷" },
  { value: "armenia", label: "Armenia", flag: "🇦🇲" },
  { value: "australia", label: "Australia", flag: "🇦🇺" },
  { value: "austria", label: "Austria", flag: "🇦🇹" },
  { value: "azerbaijan", label: "Azerbaijan", flag: "🇦🇿" },
  { value: "bahamas", label: "Bahamas", flag: "🇧🇸" },
  { value: "bahrain", label: "Bahrain", flag: "🇧🇭" },
  { value: "bangladesh", label: "Bangladesh", flag: "🇧🇩" },
  { value: "barbados", label: "Barbados", flag: "🇧🇧" },
  { value: "belarus", label: "Belarus", flag: "🇧🇾" },
  { value: "belgium", label: "Belgium", flag: "🇧🇪" },
  { value: "belize", label: "Belize", flag: "🇧🇿" },
  { value: "benin", label: "Benin", flag: "🇧🇯" },
  { value: "bhutan", label: "Bhutan", flag: "🇧🇹" },
  { value: "bolivia", label: "Bolivia", flag: "🇧🇴" },
  { value: "bosnia_and_herzegovina", label: "Bosnia and Herzegovina", flag: "🇧🇦" },
  { value: "botswana", label: "Botswana", flag: "🇧🇼" },
  { value: "brazil", label: "Brazil", flag: "🇧🇷" },
  { value: "brunei", label: "Brunei", flag: "🇧🇳" },
  { value: "bulgaria", label: "Bulgaria", flag: "🇧🇬" },
  { value: "burkina_faso", label: "Burkina Faso", flag: "🇧🇫" },
  { value: "burundi", label: "Burundi", flag: "🇧🇮" },
  { value: "cabo_verde", label: "Cabo Verde", flag: "🇨🇻" },
  { value: "cambodia", label: "Cambodia", flag: "🇰🇭" },
  { value: "cameroon", label: "Cameroon", flag: "🇨🇲" },
  { value: "canada", label: "Canada", flag: "🇨🇦" },
  { value: "car", label: "Central African Republic", flag: "🇨🇫" },
  { value: "chad", label: "Chad", flag: "🇹🇩" },
  { value: "chile", label: "Chile", flag: "🇨🇱" },
  { value: "china", label: "China", flag: "🇨🇳" },
  { value: "colombia", label: "Colombia", flag: "🇨🇴" },
  { value: "comoros", label: "Comoros", flag: "🇰🇲" },
  { value: "congo", label: "Congo", flag: "🇨🇬" },
  { value: "drc", label: "DR Congo", flag: "🇨🇩" },
  { value: "costa_rica", label: "Costa Rica", flag: "🇨🇷" },
  { value: "cote_divoire", label: "Côte d'Ivoire", flag: "🇨🇮" },
  { value: "croatia", label: "Croatia", flag: "🇭🇷" },
  { value: "cuba", label: "Cuba", flag: "🇨🇺" },
  { value: "cyprus", label: "Cyprus", flag: "🇨🇾" },
  { value: "czech_republic", label: "Czech Republic", flag: "🇨🇿" },
  { value: "denmark", label: "Denmark", flag: "🇩🇰" },
  { value: "djibouti", label: "Djibouti", flag: "🇩🇯" },
  { value: "dominica", label: "Dominica", flag: "🇩🇲" },
  { value: "dominican_republic", label: "Dominican Republic", flag: "🇩🇴" },
  { value: "ecuador", label: "Ecuador", flag: "🇪🇨" },
  { value: "egypt", label: "Egypt", flag: "🇪🇬" },
  { value: "el_salvador", label: "El Salvador", flag: "🇸🇻" },
  { value: "equatorial_guinea", label: "Equatorial Guinea", flag: "🇬🇶" },
  { value: "eritrea", label: "Eritrea", flag: "🇪🇷" },
  { value: "estonia", label: "Estonia", flag: "🇪🇪" },
  { value: "eswatini", label: "Eswatini", flag: "🇸🇿" },
  { value: "ethiopia", label: "Ethiopia", flag: "🇪🇹" },
  { value: "fiji", label: "Fiji", flag: "🇫🇯" },
  { value: "finland", label: "Finland", flag: "🇫🇮" },
  { value: "france", label: "France", flag: "🇫🇷" },
  { value: "gabon", label: "Gabon", flag: "🇬🇦" },
  { value: "gambia", label: "Gambia", flag: "🇬🇲" },
  { value: "georgia", label: "Georgia", flag: "🇬🇪" },
  { value: "germany", label: "Germany", flag: "🇩🇪" },
  { value: "ghana", label: "Ghana", flag: "🇬🇭" },
  { value: "greece", label: "Greece", flag: "🇬🇷" },
  { value: "grenada", label: "Grenada", flag: "🇬🇩" },
  { value: "guatemala", label: "Guatemala", flag: "🇬🇹" },
  { value: "guinea", label: "Guinea", flag: "🇬🇳" },
  { value: "guinea_bissau", label: "Guinea-Bissau", flag: "🇬🇼" },
  { value: "guyana", label: "Guyana", flag: "🇬🇾" },
  { value: "haiti", label: "Haiti", flag: "🇭🇹" },
  { value: "honduras", label: "Honduras", flag: "🇭🇳" },
  { value: "hungary", label: "Hungary", flag: "🇭🇺" },
  { value: "iceland", label: "Iceland", flag: "🇮🇸" },
  { value: "india", label: "India", flag: "🇮🇳" },
  { value: "indonesia", label: "Indonesia", flag: "🇮🇩" },
  { value: "iran", label: "Iran", flag: "🇮🇷" },
  { value: "iraq", label: "Iraq", flag: "🇮🇶" },
  { value: "ireland", label: "Ireland", flag: "🇮🇪" },
  { value: "israel", label: "Israel", flag: "🇮🇱" },
  { value: "italy", label: "Italy", flag: "🇮🇹" },
  { value: "jamaica", label: "Jamaica", flag: "🇯🇲" },
  { value: "japan", label: "Japan", flag: "🇯🇵" },
  { value: "jordan", label: "Jordan", flag: "🇯🇴" },
  { value: "kazakhstan", label: "Kazakhstan", flag: "🇰🇿" },
  { value: "kenya", label: "Kenya", flag: "🇰🇪" },
  { value: "kiribati", label: "Kiribati", flag: "🇰🇮" },
  { value: "north_korea", label: "North Korea", flag: "🇰🇵" },
  { value: "south_korea", label: "South Korea", flag: "🇰🇷" },
  { value: "kuwait", label: "Kuwait", flag: "🇰🇼" },
  { value: "kyrgyzstan", label: "Kyrgyzstan", flag: "🇰���" },
  { value: "laos", label: "Laos", flag: "🇱🇦" },
  { value: "latvia", label: "Latvia", flag: "🇱🇻" },
  { value: "lebanon", label: "Lebanon", flag: "🇱🇧" },
  { value: "lesotho", label: "Lesotho", flag: "🇱🇸" },
  { value: "liberia", label: "Liberia", flag: "🇱🇷" },
  { value: "libya", label: "Libya", flag: "🇱🇾" },
  { value: "liechtenstein", label: "Liechtenstein", flag: "🇱🇮" },
  { value: "lithuania", label: "Lithuania", flag: "🇱🇹" },
  { value: "luxembourg", label: "Luxembourg", flag: "🇱🇺" },
  { value: "madagascar", label: "Madagascar", flag: "🇲🇬" },
  { value: "malawi", label: "Malawi", flag: "🇲🇼" },
  { value: "malaysia", label: "Malaysia", flag: "🇲🇾" },
  { value: "maldives", label: "Maldives", flag: "🇲🇻" },
  { value: "mali", label: "Mali", flag: "🇲🇱" },
  { value: "malta", label: "Malta", flag: "🇲🇹" },
  { value: "marshall_islands", label: "Marshall Islands", flag: "🇲🇭" },
  { value: "mauritania", label: "Mauritania", flag: "🇲🇷" },
  { value: "mauritius", label: "Mauritius", flag: "🇲🇺" },
  { value: "mexico", label: "Mexico", flag: "🇲🇽" },
  { value: "micronesia", label: "Micronesia", flag: "🇫🇲" },
  { value: "moldova", label: "Moldova", flag: "🇲🇩" },
  { value: "monaco", label: "Monaco", flag: "🇲🇨" },
  { value: "mongolia", label: "Mongolia", flag: "🇲🇳" },
  { value: "montenegro", label: "Montenegro", flag: "🇲🇪" },
  { value: "morocco", label: "Morocco", flag: "🇲🇦" },
  { value: "mozambique", label: "Mozambique", flag: "🇲🇿" },
  { value: "myanmar", label: "Myanmar", flag: "🇲🇲" },
  { value: "namibia", label: "Namibia", flag: "🇳🇦" },
  { value: "nauru", label: "Nauru", flag: "🇳🇷" },
  { value: "nepal", label: "Nepal", flag: "🇳🇵" },
  { value: "netherlands", label: "Netherlands", flag: "🇳🇱" },
  { value: "new_zealand", label: "New Zealand", flag: "🇳🇿" },
  { value: "nicaragua", label: "Nicaragua", flag: "🇳🇮" },
  { value: "niger", label: "Niger", flag: "🇳🇪" },
  { value: "nigeria", label: "Nigeria", flag: "🇳🇬" },
  { value: "north_macedonia", label: "North Macedonia", flag: "🇲🇰" },
  { value: "norway", label: "Norway", flag: "🇳🇴" },
  { value: "oman", label: "Oman", flag: "🇴🇲" },
  { value: "pakistan", label: "Pakistan", flag: "🇵🇰" },
  { value: "palau", label: "Palau", flag: "🇵🇼" },
  { value: "palestine", label: "Palestine", flag: "🇵🇸" },
  { value: "panama", label: "Panama", flag: "🇵🇦" },
  { value: "papua_new_guinea", label: "Papua New Guinea", flag: "🇵🇬" },
  { value: "paraguay", label: "Paraguay", flag: "🇵🇾" },
  { value: "peru", label: "Peru", flag: "🇵🇪" },
  { value: "philippines", label: "Philippines", flag: "🇵🇭" },
  { value: "poland", label: "Poland", flag: "🇵🇱" },
  { value: "portugal", label: "Portugal", flag: "🇵🇹" },
  { value: "qatar", label: "Qatar", flag: "🇶🇦" },
  { value: "romania", label: "Romania", flag: "🇷🇴" },
  { value: "russia", label: "Russia", flag: "🇷🇺" },
  { value: "rwanda", label: "Rwanda", flag: "🇷🇼" },
  { value: "saint_kitts_and_nevis", label: "Saint Kitts and Nevis", flag: "🇰🇳" },
  { value: "saint_lucia", label: "Saint Lucia", flag: "🇱🇨" },
  { value: "saint_vincent_and_the_grenadines", label: "Saint Vincent and the Grenadines", flag: "🇻🇨" },
  { value: "samoa", label: "Samoa", flag: "🇼🇸" },
  { value: "san_marino", label: "San Marino", flag: "🇸🇲" },
  { value: "sao_tome_and_principe", label: "Sao Tome and Principe", flag: "🇸🇹" },
  { value: "saudi_arabia", label: "Saudi Arabia", flag: "🇸🇦" },
  { value: "senegal", label: "Senegal", flag: "🇸🇳" },
  { value: "serbia", label: "Serbia", flag: "🇷🇸" },
  { value: "seychelles", label: "Seychelles", flag: "🇸🇨" },
  { value: "sierra_leone", label: "Sierra Leone", flag: "🇸🇱" },
  { value: "singapore", label: "Singapore", flag: "🇸🇬" },
  { value: "slovakia", label: "Slovakia", flag: "🇸🇰" },
  { value: "slovenia", label: "Slovenia", flag: "🇸🇮" },
  { value: "solomon_islands", label: "Solomon Islands", flag: "🇸🇧" },
  { value: "somalia", label: "Somalia", flag: "🇸🇴" },
  { value: "south_africa", label: "South Africa", flag: "🇿🇦" },
  { value: "south_sudan", label: "South Sudan", flag: "🇸🇸" },
  { value: "spain", label: "Spain", flag: "🇪🇸" },
  { value: "sri_lanka", label: "Sri Lanka", flag: "🇱🇰" },
  { value: "sudan", label: "Sudan", flag: "🇸🇩" },
  { value: "suriname", label: "Suriname", flag: "🇸🇷" },
  { value: "sweden", label: "Sweden", flag: "🇸🇪" },
  { value: "switzerland", label: "Switzerland", flag: "🇨🇭" },
  { value: "syria", label: "Syria", flag: "🇸🇾" },
  { value: "taiwan", label: "Taiwan", flag: "🇹🇼" },
  { value: "tajikistan", label: "Tajikistan", flag: "🇹🇯" },
  { value: "tanzania", label: "Tanzania", flag: "🇹🇿" },
  { value: "thailand", label: "Thailand", flag: "🇹🇭" },
  { value: "timor_leste", label: "Timor-Leste", flag: "🇹🇱" },
  { value: "togo", label: "Togo", flag: "🇹🇬" },
  { value: "tonga", label: "Tonga", flag: "🇹🇴" },
  { value: "trinidad_and_tobago", label: "Trinidad and Tobago", flag: "🇹🇹" },
  { value: "tunisia", label: "Tunisia", flag: "🇹🇳" },
  { value: "turkey", label: "Turkey", flag: "🇹🇷" },
  { value: "turkmenistan", label: "Turkmenistan", flag: "🇹🇲" },
  { value: "tuvalu", label: "Tuvalu", flag: "🇹🇻" },
  { value: "uganda", label: "Uganda", flag: "🇺🇬" },
  { value: "ukraine", label: "Ukraine", flag: "🇺🇦" },
  { value: "uae", label: "United Arab Emirates", flag: "🇦🇪" },
  { value: "uk", label: "United Kingdom", flag: "🇬🇧" },
  { value: "usa", label: "United States", flag: "🇺🇸" },
  { value: "uruguay", label: "Uruguay", flag: "🇺🇾" },
  { value: "uzbekistan", label: "Uzbekistan", flag: "🇺🇿" },
  { value: "vanuatu", label: "Vanuatu", flag: "🇻🇺" },
  { value: "vatican_city", label: "Vatican City", flag: "🇻🇦" },
  { value: "venezuela", label: "Venezuela", flag: "🇻🇪" },
  { value: "vietnam", label: "Vietnam", flag: "🇻🇳" },
  { value: "yemen", label: "Yemen", flag: "🇾🇪" },
  { value: "zambia", label: "Zambia", flag: "🇿🇲" },
  { value: "zimbabwe", label: "Zimbabwe", flag: "🇿🇼" },
];

const travelPurposeOptions = [
  { value: "student", label: "Student", icon: <GraduationCap className="h-5 w-5" /> },
  { value: "worker", label: "Worker", icon: <BriefcaseIcon className="h-5 w-5" /> },
  { value: "business", label: "Business", icon: <Plane className="h-5 w-5" /> },
  { value: "tourist", label: "Tourist", icon: <MapPin className="h-5 w-5" /> },
  { value: "other", label: "Other", icon: <UserCheck className="h-5 w-5" /> },
];

const formSchema = z.object({
  destination: z.string().min(1, "Please select a destination country"),
  otherDestination: z.string().optional(),
  purpose: z.string().min(1, "Please select your travel purpose"),
  currentLocation: z.string().min(1, "Please select your current location"),
  name: z.string().min(1, "Please enter your name"),
  email: z.string().email("Please enter a valid email address"),
  visaType: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const TravelSurveyPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<SurveyStep>("destination");
  const [progress, setProgress] = useState<number>(25);
  const [showOtherCountries, setShowOtherCountries] = useState<boolean>(false);
  const [animationKey, setAnimationKey] = useState<number>(0);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      destination: "",
      otherDestination: "",
      purpose: "",
      currentLocation: "",
      name: "",
      email: "",
      visaType: "",
    },
  });

  const destination = form.watch("destination");

  const triggerAnimation = () => {
    setAnimationKey(prev => prev + 1);
  };

  const handleSubmitStep = async () => {
    try {
      switch (step) {
        case "destination":
          await form.trigger("destination");
          if (form.getFieldState("destination").invalid) return;
          if (destination === "other" && !showOtherCountries) {
            setShowOtherCountries(true);
            return;
          }
          triggerAnimation();
          setTimeout(() => {
            setStep("purpose");
            setProgress(50);
          }, 300);
          break;
        case "purpose":
          await form.trigger("purpose");
          if (form.getFieldState("purpose").invalid) return;
          triggerAnimation();
          setTimeout(() => {
            setStep("location");
            setProgress(75);
          }, 300);
          break;
        case "location":
          await form.trigger("currentLocation");
          if (form.getFieldState("currentLocation").invalid) return;
          triggerAnimation();
          setTimeout(() => {
            setStep("contact");
            setProgress(100);
          }, 300);
          break;
        case "contact":
          await form.trigger(["name", "email"]);
          if (
            form.getFieldState("name").invalid ||
            form.getFieldState("email").invalid
          )
            return;
          triggerAnimation();
          setTimeout(() => {
            setStep("complete");
          }, 300);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error advancing step:", error);
    }
  };

  const handlePreviousStep = () => {
    triggerAnimation();
    setTimeout(() => {
      if (step === "purpose") {
        setStep("destination");
        setProgress(25);
      } else if (step === "location") {
        setStep("purpose");
        setProgress(50);
      } else if (step === "contact") {
        setStep("location");
        setProgress(75);
      }
    }, 300);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const { error } = await supabase.from('travel_surveys').insert({
        destination_country: data.destination === 'other' ? data.otherDestination : data.destination,
        visa_type: data.visaType || null,
        travel_purpose: data.purpose,
        current_location: data.currentLocation,
        name: data.name,
        email: data.email,
        user_id: (await supabase.auth.getUser()).data.user?.id || null
      });

      if (error) {
        console.error('Error saving survey:', error);
        toast.error("There was an error saving your information. Please try again.");
        return;
      }

      toast.success("Survey completed! Welcome to Travel Buddy!", {
        description: `We'll send personalized travel opportunities to ${data.email}`,
      });
      
      setTimeout(() => {
        navigate("/tools-selection");
      }, 2000);
    } catch (error) {
      console.error('Error in form submission:', error);
      toast.error("There was an error saving your information. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 to-purple-50">
      <Navbar />
      
      <div className="flex-grow flex items-center justify-center py-10 px-4">
        <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-100 rounded-full opacity-50"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-100 rounded-full opacity-50"></div>
          
          <div className="relative z-10">
            <div className="mb-10 text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                {step === "complete" ? "Thank You!" : "Start Your Travel Journey"}
              </h1>
              <p className="text-gray-600 max-w-xl mx-auto">
                {step === "complete"
                  ? "Welcome to Travel Buddy! Explore our platform to find the best opportunities."
                  : "Tell us about your travel plans and we'll help you find the perfect opportunities."}
              </p>
            </div>

            {step !== "complete" && (
              <div className="w-full mb-10">
                <Progress value={progress} className="h-2 mb-2" />
                <div className="flex justify-between text-sm text-gray-500">
                  <span className={step === "destination" ? "text-blue-600 font-medium" : ""}>
                    Destination
                  </span>
                  <span className={step === "purpose" ? "text-blue-600 font-medium" : ""}>
                    Purpose
                  </span>
                  <span className={step === "location" ? "text-blue-600 font-medium" : ""}>
                    Location
                  </span>
                  <span className={step === "contact" ? "text-blue-600 font-medium" : ""}>
                    Contact
                  </span>
                </div>
              </div>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div key={animationKey} className="animate-enter">
                  {step === "destination" && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-semibold text-center">Where would you like to go?</h2>
                      
                      {!showOtherCountries ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {countryOptions.map((country, index) => (
                            <FormField
                              key={country.value}
                              control={form.control}
                              name="destination"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <div
                                      className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all hover:scale-105 duration-300 hover:shadow-md ${
                                        field.value === country.value
                                          ? "border-blue-600 bg-blue-50 shadow-md"
                                          : "hover:bg-gray-50"
                                      }`}
                                      onClick={() => field.onChange(country.value)}
                                      style={{ animationDelay: `${index * 0.05}s` }}
                                    >
                                      <span className="text-2xl">{country.flag}</span>
                                      <span className="text-lg">{country.label}</span>
                                      {field.value === country.value && (
                                        <Check className="ml-auto h-5 w-5 text-blue-600" />
                                      )}
                                    </div>
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-6">
                          <div className="flex justify-between">
                            <h3 className="font-medium">Select another country</h3>
                            <button
                              type="button"
                              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                              onClick={() => setShowOtherCountries(false)}
                            >
                              <ArrowLeft className="h-4 w-4" />
                              Back to popular countries
                            </button>
                          </div>
                          
                          <FormField
                            control={form.control}
                            name="otherDestination"
                            render={({ field }) => (
                              <FormItem>
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  defaultValue={field.value}
                                >
                                  <FormControl>
                                    <SelectTrigger className="w-full h-14">
                                      <SelectValue placeholder="Select a country" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {allCountries.filter(c => !countryOptions.some(co => co.value === c.value && co.value !== "other")).map((country) => (
                                      <SelectItem key={country.value} value={country.value}>
                                        <div className="flex items-center gap-2">
                                          <span className="text-lg">{country.flag}</span>
                                          <span>{country.label}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {step === "purpose" && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-semibold text-center">What's your purpose for traveling?</h2>
                      
                      <FormField
                        control={form.control}
                        name="purpose"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                                className="grid grid-cols-1 gap-4"
                              >
                                {travelPurposeOptions.map((purpose, index) => (
                                  <FormItem key={purpose.value}>
                                    <FormControl>
                                      <div
                                        className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all hover:scale-105 duration-300 hover:shadow-md ${
                                          field.value === purpose.value
                                            ? "border-blue-600 bg-blue-50 shadow-md"
                                            : "hover:bg-gray-50"
                                        }`}
                                        onClick={() => field.onChange(purpose.value)}
                                        style={{ animationDelay: `${index * 0.05}s` }}
                                      >
                                        <div className="flex h-12 w-12 rounded-full bg-blue-100 items-center justify-center text-blue-600">
                                          {purpose.icon}
                                        </div>
                                        <span className="text-lg">{purpose.label}</span>
                                        <RadioGroupItem
                                          value={purpose.value}
                                          className="ml-auto"
                                          checked={field.value === purpose.value}
                                        />
                                      </div>
                                    </FormControl>
                                  </FormItem>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {step === "location" && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-semibold text-center">Where are you currently located?</h2>
                      
                      <FormField
                        control={form.control}
                        name="currentLocation"
                        render={({ field }) => (
                          <FormItem className="animate-fade-in">
                            <FormControl>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <SelectTrigger className="w-full h-14">
                                  <SelectValue placeholder="Select your current location" />
                                </SelectTrigger>
                                <SelectContent>
                                  {allCountries.map((country) => (
                                    <SelectItem key={country.value} value={country.value}>
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{country.flag}</span>
                                        <span>{country.label}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {step === "contact" && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-semibold text-center">
                        Just a few details to personalize your experience
                      </h2>
                      <p className="text-center text-gray-500">
                        We'll email you personalized travel opportunities based on your preferences.
                      </p>
                      
                      <div className="grid grid-cols-1 gap-6 max-w-md mx-auto">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Full Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Your name" {...field} className="h-12" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email Address</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="you@example.com" {...field} className="h-12" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {step === "complete" && (
                    <div className="space-y-8 text-center py-6">
                      <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center text-green-600 mx-auto animate-scale-in">
                        <Check className="w-12 h-12" />
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold mb-3">Welcome aboard!</h2>
                        <p className="text-gray-600 max-w-md mx-auto">
                          We've received your preferences and will send you personalized travel opportunities.
                          You're all set to explore Travel Buddy!
                        </p>
                      </div>
                      
                      <div className="pt-6">
                        <Button 
                          type="button" 
                          onClick={() => navigate("/tools-selection")}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 h-auto text-lg rounded-lg"
                        >
                          Start Exploring
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {step !== "complete" && (
                  <div className={`flex ${step !== "destination" ? "justify-between" : "justify-end"} pt-6`}>
                    {step !== "destination" && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handlePreviousStep}
                        className="min-w-32 flex items-center gap-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                      </Button>
                    )}
                    <Button
                      type="button"
                      onClick={handleSubmitStep}
                      className="bg-blue-600 hover:bg-blue-700 min-w-32"
                    >
                      {step === "contact" ? "Complete" : "Continue"}
                    </Button>
                  </div>
                )}
              </form>
            </Form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TravelSurveyPage;
